import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../ui/Card';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Button } from '../ui/Button';
import { Table } from '../ui/Table';
import type { AdminIpWhitelistEntry } from '../../types/admin';

const typeOptions = [
  { value: 'single', label: 'Single IP' },
  { value: 'cidr', label: 'CIDR' },
  { value: 'range', label: 'Range' },
  { value: 'cdn', label: 'CDN' },
];

interface IpWhitelistManagerProps {
  entries: AdminIpWhitelistEntry[];
  onAdd: (payload: Partial<AdminIpWhitelistEntry>) => void;
  onDelete: (entry: AdminIpWhitelistEntry) => void;
}

export const IpWhitelistManager: React.FC<IpWhitelistManagerProps> = ({
  entries,
  onAdd,
  onDelete,
}) => {
  const { t } = useTranslation('admin');
  const [form, setForm] = useState({
    type: 'single',
    ip_address: '',
    cidr_range: '',
    range_start: '',
    range_end: '',
    source: '',
    description: '',
    expires_at: '',
  });

  const handleAdd = () => {
    onAdd({
      type: form.type as AdminIpWhitelistEntry['type'],
      ip_address: form.ip_address || undefined,
      cidr_range: form.cidr_range || undefined,
      range_start: form.range_start || undefined,
      range_end: form.range_end || undefined,
      source: form.source || undefined,
      description: form.description || undefined,
      expires_at: form.expires_at || undefined,
    });
    setForm({
      type: 'single',
      ip_address: '',
      cidr_range: '',
      range_start: '',
      range_end: '',
      source: '',
      description: '',
      expires_at: '',
    });
  };

  const exportCsv = () => {
    const headers = ['id', 'type', 'ip_address', 'cidr_range', 'range_start', 'range_end', 'source', 'description', 'expires_at'];
    const rows = entries.map((entry) => headers.map((key) => (entry as any)[key] ?? '').join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'ip-whitelist.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const columns = [
    { key: 'id', header: t('rate_limits.whitelist.table.id') },
    { key: 'type', header: t('rate_limits.whitelist.table.type') },
    { key: 'ip_address', header: t('rate_limits.whitelist.table.ip') },
    { key: 'cidr_range', header: t('rate_limits.whitelist.table.cidr') },
    { key: 'range_start', header: t('rate_limits.whitelist.table.range_start') },
    { key: 'range_end', header: t('rate_limits.whitelist.table.range_end') },
    { key: 'expires_at', header: t('rate_limits.whitelist.table.expires') },
  ];

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">{t('rate_limits.whitelist.title')}</h3>
        <Button variant="secondary" onClick={exportCsv}>
          {t('rate_limits.whitelist.export')}
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('rate_limits.whitelist.type')}</label>
          <Select
            value={form.type}
            onChange={(event) => setForm((prev) => ({ ...prev, type: event.target.value }))}
            options={typeOptions}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('rate_limits.whitelist.ip')}</label>
          <Input
            value={form.ip_address}
            onChange={(event) => setForm((prev) => ({ ...prev, ip_address: event.target.value }))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('rate_limits.whitelist.cidr')}</label>
          <Input
            value={form.cidr_range}
            onChange={(event) => setForm((prev) => ({ ...prev, cidr_range: event.target.value }))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('rate_limits.whitelist.range_start')}</label>
          <Input
            value={form.range_start}
            onChange={(event) => setForm((prev) => ({ ...prev, range_start: event.target.value }))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('rate_limits.whitelist.range_end')}</label>
          <Input
            value={form.range_end}
            onChange={(event) => setForm((prev) => ({ ...prev, range_end: event.target.value }))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('rate_limits.whitelist.source')}</label>
          <Input
            value={form.source}
            onChange={(event) => setForm((prev) => ({ ...prev, source: event.target.value }))}
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('rate_limits.whitelist.description')}</label>
          <Input
            value={form.description}
            onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('rate_limits.whitelist.expires')}</label>
          <Input
            type="date"
            value={form.expires_at}
            onChange={(event) => setForm((prev) => ({ ...prev, expires_at: event.target.value }))}
          />
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <Button onClick={handleAdd}>{t('rate_limits.whitelist.add')}</Button>
      </div>

      <div className="mt-6">
        <Table
          columns={columns}
          data={entries}
          renderActions={(entry) => (
            <Button variant="danger" size="sm" onClick={() => onDelete(entry)}>
              {t('rate_limits.whitelist.remove')}
            </Button>
          )}
        />
      </div>
    </Card>
  );
};

export default IpWhitelistManager;
